import { RenderEngine } from "../3DController/core/RenderEngine";
import { LogoController } from "../3DController/LogoController";
import { EngravingController } from "../3DController/EngravingController";
import { IRingState } from "../RingStateController/RingStateController";
import { ISavedRingConfiguration } from "../../sharedInterfaces";
import { IFingerSizeFormat } from "../../Model/RingSizes";
import { RequestService } from "../../Service/RequestService";
import { CaptureController } from "../3DController/Captuire/CaptureController";

/**
 * Wrapper for individual controllers/ handlers.
 */

export interface MainControllerArguments {
  renderEngine:RenderEngine,
  requestService:RequestService,
  assetURI:string,
  logoStyle:'ifr'|'luxe',
  getRingState:()=>IRingState,
  saveRingConfiguration:()=>Promise<string>,
  onUpdate:()=>void
} 

export class MainController {
  private _renderEngine:RenderEngine;
  private _requestService:RequestService;
  private _assetURI:string;
  private _getRingState:()=>IRingState;
  private _saveRingConfiguration:()=>Promise<string>;
  private _onUpdate:()=>void;

  private _logoController:LogoController;
  private _engravingController:EngravingController;
  private _captureController:CaptureController;
  
  public get logoController() { return this._logoController };
  public get captureController() { return this._captureController };

  constructor(mainControllerArguments:MainControllerArguments) {
    this._renderEngine = mainControllerArguments.renderEngine;
    this._requestService = mainControllerArguments.requestService;
    this._assetURI = mainControllerArguments.assetURI;
    this._getRingState = mainControllerArguments.getRingState;
    this._saveRingConfiguration = mainControllerArguments.saveRingConfiguration;
    this._onUpdate = mainControllerArguments.onUpdate;

    this._logoController = new LogoController(this._renderEngine, this._assetURI, mainControllerArguments.logoStyle);
    this._engravingController = new EngravingController(this._renderEngine);
    this._captureController = new CaptureController({
      renderEngine: this._renderEngine,
      requestService: this._requestService,
      logoController: this._logoController,
      getRingState: this._getRingState,
      saveRingConfiguration: this._saveRingConfiguration,
      onUpdate: this._onUpdate
    })
  }

  public initialize = async ():Promise<void> => {
    await this._engravingController.initialize();
    return Promise.resolve();
  }

  public initializeCaptureController = async (captureConfigId?:string):Promise<void> => {
    this.captureController.initialize(captureConfigId);
  }

  public updateEngraving = (ringState:IRingState) => {
    
    this._engravingController.updateEngraving(ringState.ringModel, ringState.width, ringState.engravingText, ringState.engravingFont);
  }

  public ringStateToSavedRingConfiguration = (ringState:IRingState, fingerSizeFormat:IFingerSizeFormat):ISavedRingConfiguration => {
    const ringConfiguration:ISavedRingConfiguration = {
      SKU: ringState.ringModel.SKU,
      fingerSize: ringState.fingerSize,
      fingerSizeFormat: fingerSizeFormat.id,
      width: ringState.width,
      thickness: ringState.thickness,
      stoneSize: ringState.stoneSize,
      metalGroups: ringState.metalGroups.map((mg) => {
        return {
          name: mg.name,
          material: mg.material,
          surface: mg.surface,
          purity: mg.purity
        }
      }),
      stoneGroups: ringState.stoneGroups.map((sg) => {
        return {
          name: sg.name,
          stone: sg.stone
        }
      }),
      engravingText: ringState.engravingText,
      engravingFont: ringState.engravingFont
    };

    return ringConfiguration;
  }

  public saveRingConfiguration = (ringState:IRingState, fingerSizeFormat:IFingerSizeFormat):Promise<string> => {
    const ringConfiguration = this.ringStateToSavedRingConfiguration(ringState, fingerSizeFormat);

    return new Promise((resolve, reject) => {
      this._requestService.saveConfiguration(ringConfiguration)
      .then((res) => {
        resolve(res);
      });
    });
  }

}// /class